import { CONTRACT, useContract } from '@apeiron/contract'
import {
  BattlePassType,
  createStrictContext,
  MayNull,
  useAccountContext,
  useMessageContext
} from '@apeiron/temp'
import { ROUTE } from '@src/constants/route'
import useGetGameBattlePassSchedules from '@src/hooks/graphql/useGetGameBattlePassSchedules'
import useFetchBattleDemoFragmentJson from '@src/hooks/json/useFetchBattleDemoFragmentJson'
import { BattleDemoFragment } from '@src/types/battleDemo'
import * as R from 'ramda'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import useAsyncEffect from 'use-async-effect'

type IBattlepassContext = {
  burnCount: number
  burnSuccess: boolean
  fragments: BattleDemoFragment[]
  battlePassSchedules: MayNull<BattlePassType[]>
  reloadData: () => Promise<void>
  setBurnCount: Dispatch<SetStateAction<number>>
  setBurnSuccess: Dispatch<SetStateAction<boolean>>
}

const [ContextProvider, useBattlepassContext] =
  createStrictContext<IBattlepassContext>('Battlepass')

export { useBattlepassContext }

export const BattlepassProvider = (props: Props) => {
  const { children } = props

  const [burnCount, setBurnCount] = useState<number>(1)

  const [burnSuccess, setBurnSuccess] = useState<boolean>(false)

  const [fragments, setFragments] = useState<BattleDemoFragment[]>([])

  const [battlePassSchedules, setBattlePassSchedules] =
    useState<MayNull<BattlePassType[]>>(null)

  const { account, fetchDetail, setShowBindWalletPopup } = useAccountContext()

  const { walletAddress } = account

  const { fetch } = useGetGameBattlePassSchedules()

  const { data } = useFetchBattleDemoFragmentJson()

  const { setInfo } = useMessageContext()

  const { t } = useTranslation()

  const { call: fetchFragmentTotal } = useContract(
    CONTRACT.BATTLE_DEMO.FRAGMENT
  )

  const injectFragmentTotal = useCallback(
    async (input: BattleDemoFragment[]) => {
      const output = R.clone(input)

      const fragmentIds = R.pluck('id', output)

      if (
        !account.walletAddress &&
        window.location.pathname === ROUTE.BATTLE_DEMO_BATTLE_PASS
      ) {
        setInfo({
          title: t('battle_demo.pass.no_wallet_found'),
          message: t('battle_demo.pass.bind_wallet'),
          cancel: t('common.cancel'),
          confirm: t('account_menu.bind_wallet'),
          onConfirm: () => {
            setShowBindWalletPopup(true)
            setInfo(null)
          }
        })

        return []
      }

      const addresses = R.repeat(account.walletAddress, R.length(output))

      const allTotal = await fetchFragmentTotal(addresses, fragmentIds)

      if (R.isNotNil(allTotal)) {
        allTotal.forEach((value: number, index: number) => {
          output[index].total = value
        })
      }

      return output
    },
    [
      account.walletAddress,
      fetchFragmentTotal,
      setInfo,
      setShowBindWalletPopup,
      t
    ]
  )

  const reloadData = useCallback(async () => {
    await fetchDetail()

    const injected = await injectFragmentTotal(fragments)

    setFragments(injected)
  }, [fetchDetail, fragments, injectFragmentTotal])

  useAsyncEffect(
    async (isActive: () => boolean) => {
      if (R.isNotNil(data)) {
        const injected = await injectFragmentTotal(data)

        if (!isActive()) {
          return
        }

        setFragments(injected)
      }
    },
    [data]
  )

  useAsyncEffect(async () => {
    if (walletAddress) {
      const data = await fetch({ userID: walletAddress })

      setBattlePassSchedules(data)
    }
  }, [walletAddress])

  return (
    <ContextProvider
      value={{
        burnSuccess,
        burnCount,
        fragments,
        battlePassSchedules,
        reloadData,
        setBurnSuccess,
        setBurnCount
      }}
    >
      {children}
    </ContextProvider>
  )
}

type Props = {
  children: ReactNode
}
